'use client';

import { faRotate, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@pickleballinc/ui/components/ui/dialog';
import { Input } from '@pickleballinc/ui/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useParams } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';

import ToggleButtonForm from '@/components/atoms/ToggleButtonForm';
import { ActionButtons, DatetimePickerForm } from '@/components/molecules';
import { EditTeamSettingsData, editTeamSettingsSchema } from '@/entities';
import { InvitationCodeData } from '@/entities/InvitationCodeData';
import { TEAM_ACTIVITY_QUERY_KEY } from '@/hooks/constants';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useCreateInvitationCodeMutate, useUpdateInvitationCodeMutate, useUpdateIsFreeAgentMutate } from '@/hooks/hooks';
import { useOrganizationStore } from '@/store/organization-store';

interface EditInvitationCodeModalProps {
	open: boolean;
	onClose: () => void;
	invitationCode: InvitationCodeData | null;
	teamName: string;
}

const isDateBeforeToday = (dateString: string | undefined | null): boolean => {
	return dateString ? dayjs(dateString).isBefore(dayjs(), 'day') : false;
};

const isDateDisabled = (dateDisabled: string | undefined | null): boolean => {
	if (!dateDisabled) return false;
	return dateDisabled !== '0001-01-01T00:00:00Z';
};

const generateRandomCode = (): string => {
	const characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const length = 6;
	return Array.from({ length }, () => {
		const randomIndex = Math.floor(Math.random() * characterSet.length);
		return characterSet[randomIndex];
	}).join('');
};

export const EditInvitationCodeModal = ({ open, onClose, invitationCode }: EditInvitationCodeModalProps) => {
	const store = useOrganizationStore();
	const params = useParams<{ teamId: string }>();
	const teamLeagueInfo = useTeamLeagueInfo();
	const queryClient = useQueryClient();

	const updateIsFreeAgent = useUpdateIsFreeAgentMutate();
	const updateInvitationCode = useUpdateInvitationCodeMutate();
	const createInvitationCode = useCreateInvitationCodeMutate();

	const form = useForm<EditTeamSettingsData>({
		resolver: zodResolver(editTeamSettingsSchema),
		defaultValues: {
			invitationCode: invitationCode?.code,
			isActive: !isDateDisabled(invitationCode?.dateDisabled),
			expirationDate: invitationCode?.dateOfExpiry || undefined,
			numberOfTimesCodeCanBeUsed: invitationCode?.numberOfUses || '',
			isFreeAgent: invitationCode?.allowFreeAgentRequests ?? false
		}
	});

	const { formState, register, handleSubmit, watch, setValue } = form;

	const handleSubmitForm = async (data: EditTeamSettingsData) => {
		if ((data.isFreeAgent ?? false) !== (invitationCode?.allowFreeAgentRequests ?? false)) {
			await updateIsFreeAgent.mutateAsync({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: params.teamId,
				requestPayload: { allow_free_agent_requests: data.isFreeAgent }
			});
		}

		if (data.invitationCode && (!invitationCode?.code || isDateBeforeToday(invitationCode?.dateOfExpiry))) {
			await createInvitationCode.mutateAsync({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: params.teamId,
				requestPayload: {
					code: data.invitationCode,
					date_of_expiry: data.expirationDate,
					number_of_uses: data.numberOfTimesCodeCanBeUsed ? data.numberOfTimesCodeCanBeUsed : null
				}
			});

			queryClient.invalidateQueries({
				queryKey: [TEAM_ACTIVITY_QUERY_KEY, store.organizationUuid, store.teamLeagueUuid, teamLeagueInfo.seasonUuid, params.teamId]
			});
			onClose();
			return;
		}

		if (
			data.invitationCode &&
			invitationCode?.uuid &&
			(!isDateDisabled(invitationCode?.dateDisabled) !== data.isActive ||
				invitationCode?.dateOfExpiry !== data.expirationDate ||
				invitationCode?.numberOfUses !== (data.numberOfTimesCodeCanBeUsed === '' ? null : data.numberOfTimesCodeCanBeUsed) ||
				invitationCode?.code !== data.invitationCode)
		) {
			const dateDisabled = !data.isActive ? dayjs().toISOString() : null;

			await updateInvitationCode.mutateAsync({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: params.teamId,
				codeUuid: invitationCode.uuid,
				requestPayload: {
					uuid: invitationCode.uuid,
					code: data.invitationCode,
					date_of_expiry: data.expirationDate,
					number_of_uses: data.numberOfTimesCodeCanBeUsed ? data.numberOfTimesCodeCanBeUsed : null,
					date_disabled: dateDisabled
				}
			});
		}

		queryClient.invalidateQueries({
			queryKey: [TEAM_ACTIVITY_QUERY_KEY, store.organizationUuid, store.teamLeagueUuid, teamLeagueInfo.seasonUuid, params.teamId]
		});
		onClose();
	};

	const isPending = updateIsFreeAgent.isPending || updateInvitationCode.isPending || createInvitationCode.isPending;

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) onClose();
			}}
		>
			<DialogContent className="max-w-[340px] rounded-lg sm:max-w-[524px] gap-0 p-6" hideClose>
				<DialogHeader className="flex w-full items-end justify-between flex-row">
					<DialogTitle>Invitation code</DialogTitle>
					<FontAwesomeIcon icon={faTimes} size="lg" className="text-gray-500 cursor-pointer" onClick={onClose} />
				</DialogHeader>
				<FormProvider {...form}>
					<form onSubmit={handleSubmit(handleSubmitForm)} className="flex flex-col" autoComplete="off">
						<div className="mb-10 flex flex-col">
							<hr className="mt-4 h-px rounded-md bg-brand-300" />
							<div>
								<div className="flex cursor-pointer items-center justify-center gap-2 w-[120px] my-4">
									<div className="flex items-center">
										<ToggleButtonForm name="isActive" />
									</div>
									<span className="text-base font-medium leading-6 text-gray-900 block w-full">
										{watch('isActive') ? 'Active' : 'Inactive'}
									</span>
								</div>

								<div className="mt-4">
									<span className="block pb-1.5 text-sm font-medium leading-5 text-gray-700">Enter new invitation code</span>
									<div className="flex w-full items-start gap-4">
										<Input
											{...register('invitationCode')}
											maxLength={6}
											minLength={6}
											className="max-w-[320px] sm:max-w-none rounded-lg focus-visible:ring-0 border border-gray-300 shadow-xs sm:w-[320px]"
										/>
										<div
											onClick={() => setValue('invitationCode', generateRandomCode(), { shouldDirty: true })}
											className="flex h-10 cursor-pointer items-center justify-center gap-2"
										>
											<FontAwesomeIcon icon={faRotate} className="text-xl font-normal leading-5 text-gray-700" />
											<span className="text-xs font-normal leading-[18px] text-gray-700">click to generate</span>
										</div>
									</div>
									<span className="block pt-1.5 text-sm font-medium leading-5 text-gray-600">
										Codes must be unique & use 6 characters
									</span>
								</div>

								<div className="my-4 w-64">
									<span className="block pb-1.5 text-base font-semibold leading-5 text-gray-900">Expiration Date</span>
									<DatetimePickerForm
										name="expirationDate"
										placeholder="Select expiration date"
										granularity="time"
										yearRange={25}
									/>
								</div>

								<div>
									<span className="block pb-1.5 text-sm font-medium leading-5 text-gray-700">Number of times code can be used</span>
									<Input
										{...register('numberOfTimesCodeCanBeUsed')}
										type="number"
										min={0}
										className="w-[260px] rounded-lg focus-visible:ring-0 border border-gray-300 shadow-xs"
									/>
									<span className="block pt-1.5 text-sm font-medium leading-5 text-gray-600">Leave blank for unlimited</span>
								</div>
							</div>

							<div>
								<hr className="mb-4 mt-6 h-px shrink-0 self-stretch rounded-md bg-brand-300" />
								<div className="my-0 flex justify-between items-center gap-10">
									<p className="text-2xl font-semibold leading-8 text-gray-900 text-nowrap">Free agents</p>
									<div className="flex cursor-pointer items-center justify-end gap-2 w-[125px] sm:w-[195px]">
										<div className="flex items-center">
											<ToggleButtonForm name="isFreeAgent" />
										</div>
										<span className="text-base font-medium leading-6 text-gray-900 block w-full">
											{watch('isFreeAgent') ? 'Requests active' : 'Requests inactive'}
										</span>
									</div>
								</div>
							</div>
						</div>

						<div className="flex justify-end">
							<ActionButtons
								onClose={onClose}
								submitButtonText="Save"
								closeButtonText="Cancel"
								closeButtonClassName="w-32 text-gray-700"
								submitButtonClassName="w-32 text-white"
								submitButtonDisabled={!formState.isDirty || formState.isSubmitting || isPending}
							/>
						</div>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};
